import type { SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";
import { capacitorErrorMessage } from "@/lib/capacitorErrors";

type AgendaRow = Database["public"]["Tables"]["agenda_events"]["Row"];
type AgendaInsert = Database["public"]["Tables"]["agenda_events"]["Insert"];
type StoredEvent = Pick<AgendaRow, "id" | "external_id" | "provider_etag" | "deleted_at" | "title" | "tag" | "start_at" | "end_at" | "location" | "notes" | "all_day_start" | "all_day_end">;
const SELECT_EVENT = "id,external_id,provider_etag,deleted_at,title,tag,start_at,end_at,location,notes,all_day_start,all_day_end";
const PAGE_SIZE = 200;
const MAX_PAGES = 100;
const MAX_DEVICE_EVENTS = 20_000;
const BASELINE_PREFIX = "apple:v1:";

export interface AppleCalendarDevice {
  /** The installed EventKit bridge returns every match, without a row cap. */
  readPermission(): Promise<{ result: unknown }>;
  listEventsInRange(range: { from: number; to: number }): Promise<unknown>;
}

export interface AppleCalendarSyncResult {
  synced: number;
  total_fetched: number;
  deleted: number;
  errors: string[];
  status: "ok" | "partial";
  preserved_conflicts: number;
  window: { from: string; to: string; complete: boolean };
}

export class AppleCalendarStaleSyncError extends Error {
  constructor() { super("Apple Calendar account changed or sync was superseded"); }
}

export function appleCalendarWindow(now: number): { from: number; to: number } {
  if (!validMillis(now)) throw new Error("Invalid Apple Calendar sync time");
  // These are elapsed days. The exact same instants go to EventKit and the
  // coverage predicate; no local-midnight/DST rounding can widen deletion.
  return { from: now - 7 * 86_400_000, to: now + 30 * 86_400_000 };
}

function validMillis(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value) && Math.abs(value) <= 8.64e15;
}
function object(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}
function validWriteRows(value: unknown, expected: Set<string>): boolean {
  if (!Array.isArray(value)) return false;
  const ids = new Set<string>(), externalIds = new Set<string>();
  for (const row of value) {
    if (!object(row) || typeof row.id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(row.id) ||
      typeof row.external_id !== "string" || !expected.has(row.external_id) || ids.has(row.id) || externalIds.has(row.external_id)) return false;
    ids.add(row.id); externalIds.add(row.external_id);
  }
  return true;
}
function dateValue(value: string | null): number | null {
  if (value === null) return null;
  const n = Date.parse(value);
  return Number.isFinite(n) ? n : null;
}
function canonicalFields(row: Pick<StoredEvent, "title" | "tag" | "start_at" | "end_at" | "location" | "notes" | "all_day_start" | "all_day_end">): string {
  return JSON.stringify([
    row.title, row.tag, dateValue(row.start_at), dateValue(row.end_at),
    row.location, row.notes, row.all_day_start, row.all_day_end,
  ]);
}
async function fingerprint(row: Parameters<typeof canonicalFields>[0]): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(canonicalFields(row)));
  return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, "0")).join("");
}
function parseBaseline(value: string | null): { modified: string | null; hash: string } | null {
  if (!value?.startsWith(BASELINE_PREFIX)) return null;
  try {
    const parsed: unknown = JSON.parse(value.slice(BASELINE_PREFIX.length));
    if (!object(parsed) || (parsed.modified !== null && typeof parsed.modified !== "string") || typeof parsed.hash !== "string" || !/^[a-f0-9]{64}$/.test(parsed.hash)) return null;
    return { modified: parsed.modified as string | null, hash: parsed.hash };
  } catch { return null; }
}

interface IncomingEvent { row: AgendaInsert & Required<Pick<AgendaInsert, "external_id" | "provider_etag" | "end_at" | "location" | "notes" | "all_day_start" | "all_day_end">>; modified: string | null; hash: string }

async function normalizeEvents(reply: unknown, userId: string, range: { from: number; to: number }): Promise<{ events: Map<string, IncomingEvent>; fetched: number; complete: boolean; errors: string[] }> {
  const events = new Map<string, IncomingEvent>();
  const conflicting = new Set<string>();
  const errors: string[] = [];
  const error = (message: string) => { if (!errors.includes(message)) errors.push(message); };
  if (!object(reply) || !Array.isArray(reply.result)) return { events, fetched: 0, complete: false, errors: ["device_response_invalid"] };
  if ((reply.partial != null && reply.partial !== false) || (reply.complete != null && reply.complete !== true) ||
    (reply.hasMore != null && reply.hasMore !== false) || reply.nextCursor != null || reply.error != null ||
    (reply.errors != null && (!Array.isArray(reply.errors) || reply.errors.length))) error("device_read_partial");
  if (reply.result.length > MAX_DEVICE_EVENTS) return { events, fetched: reply.result.length, complete: false, errors: ["device_event_limit"] };
  for (const value of reply.result) {
    if (!object(value) || typeof value.id !== "string" || !value.id.trim() || value.id.length > 4096 || !validMillis(value.startDate) || !validMillis(value.endDate) || value.endDate < value.startDate) { error("device_event_invalid"); continue; }
    // A native predicate returns overlaps, including long/recurring events.
    // Data completely outside the requested range indicates a bad/partial
    // bridge response and cannot establish negative evidence.
    if (value.startDate >= range.to || value.endDate < range.from) { error("device_event_outside_window"); continue; }
    if ((value.title != null && typeof value.title !== "string") || (value.location != null && typeof value.location !== "string") || (value.description != null && typeof value.description !== "string") || (value.lastModifiedDate != null && !validMillis(value.lastModifiedDate))) { error("device_event_invalid"); continue; }
    const externalId = value.id + ":" + value.startDate;
    if (conflicting.has(externalId)) continue;
    const modified = validMillis(value.lastModifiedDate) ? String(value.lastModifiedDate) : null;
    const row = {
      user_id: userId, title: (value.title as string) || "(no title)", tag: "Meeting",
      start_at: new Date(value.startDate).toISOString(), end_at: new Date(value.endDate).toISOString(),
      location: (value.location as string) || null, notes: (value.description as string) || null,
      source: "apple_calendar", external_id: externalId, external_provider: "apple",
      // Existing mapping is timed; this block does not invent all-day dates
      // or clear an all-day edit previously made inside VYV.
      all_day_start: null, all_day_end: null, provider_etag: "", deleted_at: null,
    };
    const hash = await fingerprint(row);
    row.provider_etag = BASELINE_PREFIX + JSON.stringify({ modified, hash });
    const previous = events.get(externalId);
    if (previous && (previous.hash !== hash || previous.modified !== modified)) {
      events.delete(externalId); conflicting.add(externalId); error("device_duplicate_conflict");
    } else events.set(externalId, { row, modified, hash });
  }
  return { events, fetched: reply.result.length, complete: errors.length === 0, errors };
}

/** Only fully interior, unchanged occurrences provide safe deletion evidence. */
export function appleCalendarDeletionCovered(row: Pick<StoredEvent, "external_id" | "start_at" | "end_at">, range: { from: number; to: number }): boolean {
  const start = dateValue(row.start_at), end = dateValue(row.end_at);
  if (start === null || end === null || end < start || start <= range.from || end >= range.to) return false;
  // Local rescheduling or a legacy series ID must never look like a deleted
  // occurrence. The occurrence identifier retains the original start instant.
  const suffix = row.external_id?.slice(row.external_id.lastIndexOf(":") + 1);
  return !!suffix && Number.isFinite(Number(suffix)) && Number(suffix) === start;
}

export async function runAppleCalendarSync({ client, device, userId, isCurrent, now = Date.now() }: {
  client: Pick<SupabaseClient<Database>, "from">;
  device: AppleCalendarDevice;
  userId: string;
  isCurrent: () => boolean;
  now?: number;
}): Promise<AppleCalendarSyncResult> {
  const range = appleCalendarWindow(now);
  const result: AppleCalendarSyncResult = {
    synced: 0, total_fetched: 0, deleted: 0, errors: [], status: "ok", preserved_conflicts: 0,
    window: { from: new Date(range.from).toISOString(), to: new Date(range.to).toISOString(), complete: false },
  };
  const current = () => { if (!isCurrent()) throw new AppleCalendarStaleSyncError(); };
  const partial = (message: string) => { result.status = "partial"; if (result.errors.length < 10 && !result.errors.includes(message)) result.errors.push(message); };
  const conflict = (message: string) => { result.preserved_conflicts++; partial(message); };
  current();
  let normalized: Awaited<ReturnType<typeof normalizeEvents>>;
  try {
    const before = await device.readPermission(); current();
    if (before.result !== "granted") throw new Error("Calendar read permission not granted");
    const reply = await device.listEventsInRange(range); current();
    const after = await device.readPermission(); current();
    if (after.result !== "granted") throw new Error("Calendar read permission changed");
    normalized = await normalizeEvents(reply, userId, range); current();
  } catch (cause) {
    current(); partial("device_read: " + capacitorErrorMessage(cause)); return result;
  }
  result.total_fetched = normalized.fetched;
  normalized.errors.forEach(partial);

  // Keyset paging does not rely on a server's default 1,000-row response.
  // A failed, capped, malformed or repeating page never yields a deletion
  // base, and no writes occur until the entire owned read succeeds.
  const existing = new Map<string, StoredEvent>();
  let cursor: string | null = null;
  let allRowsRead = false;
  for (let page = 0; page < MAX_PAGES; page++) {
    current();
    let query = client.from("agenda_events").select(SELECT_EVENT, { count: "exact" }).eq("user_id", userId).eq("source", "apple_calendar").order("id", { ascending: true }).limit(PAGE_SIZE);
    if (cursor) query = query.gt("id", cursor);
    let reply: Awaited<typeof query>;
    try { reply = await query; current(); } catch (cause) { current(); partial("existing_read: " + capacitorErrorMessage(cause)); return result; }
    if (reply.error || !Array.isArray(reply.data)) { partial("existing_read: " + capacitorErrorMessage(reply.error, "invalid response")); return result; }
    const rows = reply.data;
    if (rows.length > PAGE_SIZE || typeof reply.count !== "number" || reply.count < rows.length || (reply.count > 0 && rows.length === 0)) { partial("existing_read_invalid_page"); return result; }
    for (const row of rows) {
      if (!row.id || (cursor !== null && row.id <= cursor) || (row.external_id !== null && existing.has(row.external_id))) { partial("existing_read_invalid_page"); return result; }
      if (row.external_id) existing.set(row.external_id, row);
      cursor = row.id;
    }
    // An installation may configure a server cap BELOW PAGE_SIZE. A short
    // page is complete only if the exact remaining count agrees with it.
    if (reply.count === rows.length) { allRowsRead = true; break; }
  }
  if (!allRowsRead) { partial("existing_read_limit"); return result; }
  result.window.complete = normalized.complete;

  // Every UPDATE compares the exact read version of all imported fields.
  // This works with the real schema (no invented edited_at) and rejects a
  // local edit, another sync or a tombstone racing an earlier read.
  const matchedUpdate = async (row: StoredEvent, patch: Database["public"]["Tables"]["agenda_events"]["Update"]) => {
    current();
    let query = client.from("agenda_events").update(patch).eq("user_id", userId).eq("source", "apple_calendar")
      .eq("id", row.id).eq("external_id", row.external_id!).is("deleted_at", null)
      .eq("title", row.title).eq("tag", row.tag).eq("start_at", row.start_at);
    for (const column of ["end_at", "location", "notes", "all_day_start", "all_day_end", "provider_etag"] as const) {
      query = row[column] === null ? query.is(column, null) : query.eq(column, row[column]!);
    }
    const writeQuery = query.select("id");
    let reply: Awaited<typeof writeQuery>;
    try { reply = await writeQuery; current(); }
    catch (cause) { current(); partial("event_write: " + capacitorErrorMessage(cause)); return false; }
    if (reply.error) { partial("event_write: " + capacitorErrorMessage(reply.error)); return false; }
    if (reply.data?.length !== 1 || reply.data[0].id !== row.id) { conflict("event_write_not_confirmed"); return false; }
    return true;
  };

  const inserts: IncomingEvent[] = [];
  for (const [externalId, incoming] of normalized.events) {
    current();
    const row = existing.get(externalId);
    if (!row) { inserts.push(incoming); continue; }
    if (row.deleted_at) continue;
    const currentHash = await fingerprint(row); current();
    const baseline = parseBaseline(row.provider_etag);
    if (baseline && currentHash !== baseline.hash) { conflict("local_event_change_preserved"); continue; }
    // Legacy etags are only native timestamps, never a content baseline.
    // They may be upgraded ONLY while their fields still match the device.
    if (!baseline && currentHash !== incoming.hash) { conflict("legacy_event_change_preserved"); continue; }
    if (baseline?.modified === incoming.modified && currentHash === incoming.hash) continue;
    if (await matchedUpdate(row, incoming.row)) result.synced++;
  }

  for (let start = 0; start < inserts.length; start += PAGE_SIZE) {
    current();
    const batch = inserts.slice(start, start + PAGE_SIZE);
    const query = client.from("agenda_events").upsert(batch.map(value => value.row), {
      onConflict: "user_id,source,external_id", ignoreDuplicates: true,
    }).select("id,external_id");
    let reply: Awaited<typeof query>;
    try { reply = await query; current(); }
    catch (cause) { current(); partial("event_insert: " + capacitorErrorMessage(cause)); continue; }
    const expectedIds = new Set(batch.map(event => event.row.external_id));
    if (reply.error || !validWriteRows(reply.data, expectedIds)) { partial("event_insert: " + capacitorErrorMessage(reply.error, "invalid response")); continue; }
    const returned = new Set(reply.data.map(row => row.external_id));
    result.synced += reply.data.length;
    // ON CONFLICT DO NOTHING protects concurrent tombstones. Zero rows can
    // also mean an RLS rejection: verify every omitted key before counting
    // it as a preserved concurrent row instead of claiming false success.
    const omitted = batch.filter(event => !returned.has(event.row.external_id));
    if (omitted.length) {
      const checkQuery = client.from("agenda_events").select(SELECT_EVENT).eq("user_id", userId).eq("source", "apple_calendar")
        .in("external_id", omitted.map(event => event.row.external_id));
      let check: Awaited<typeof checkQuery>;
      try { check = await checkQuery; current(); }
      catch { current(); partial("event_insert_not_confirmed"); continue; }
      if (check.error || !validWriteRows(check.data, new Set(omitted.map(event => event.row.external_id)))) { partial("event_insert_not_confirmed"); continue; }
      const found = new Map(check.data.map(row => [row.external_id, row]));
      for (const event of omitted) {
        const foundRow = found.get(event.row.external_id);
        if (!foundRow) partial("event_insert_not_confirmed");
        else if (!foundRow.deleted_at && canonicalFields(foundRow) !== canonicalFields(event.row)) conflict("concurrent_event_change_preserved");
      }
    }
  }

  // Negative evidence is trusted only after complete device and DB reads,
  // and successful imports. Partial reads/writes can NEVER remove copies.
  if (normalized.complete && result.status === "ok") {
    for (const [externalId, row] of existing) {
      current();
      if (result.errors.length) break;
      if (row.deleted_at || normalized.events.has(externalId) || !appleCalendarDeletionCovered(row, range)) continue;
      const baseline = parseBaseline(row.provider_etag);
      if (!baseline || await fingerprint(row) !== baseline.hash) { current(); conflict("unverified_absence_preserved"); continue; }
      current();
      if (await matchedUpdate(row, { deleted_at: new Date(now).toISOString() })) result.deleted++;
    }
  }
  return result;
}
